import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import type {
  AtbWeightDatabaseMode,
  AtbWeightDatabaseSettingsInput,
  AtbWeightDatabaseView,
} from '@reform-lite/contracts';
import { useHasModule, useHasPermission } from '@/auth/auth-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useT } from '@/i18n/use-t';
import {
  useAtbWeightDatabase,
  useClearAtbWeightDatabasePassword,
  useSaveAtbWeightDatabase,
  useTestAtbWeightDatabase,
} from '@/hooks/use-atb-weight-database';

/** What the panel shows in place of a stored password. */
export const PASSWORD_MASK = '••••••••';

function errorText(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string } | undefined;
    return typeof body?.message === 'string' ? body.message : null;
  }
  return null;
}

type Draft = {
  mode: AtbWeightDatabaseMode;
  server: string;
  port: string;
  databaseName: string;
  login: string;
  refreshIntervalMinutes: string;
  writeBack: boolean;
};

function draftOf(view: AtbWeightDatabaseView): Draft {
  return {
    mode: view.mode,
    server: view.server ?? '',
    port: String(view.port),
    databaseName: view.databaseName ?? '',
    login: view.login ?? '',
    refreshIntervalMinutes: String(view.refreshIntervalMinutes),
    writeBack: view.writeBack,
  };
}

const optional = (value: string): string | undefined =>
  value.trim() === '' ? undefined : value.trim();
const integer = (value: string): number | undefined =>
  /^\d+$/.test(value.trim()) ? Number(value.trim()) : undefined;

/**
 * ATBL-449 — the store's one security-weight database for its ATB
 * self-checkout lanes: the lanes' own (SQLite) or WinSCOServer on Microsoft
 * SQL. One choice, so two databases cannot be selected. The Microsoft SQL
 * fields show only for Microsoft SQL and are kept, not cleared, when the
 * store goes back to SQLite. The password is write-only: once stored it
 * shows as dots and can only be replaced or cleared. Back Office refuses to
 * save Microsoft SQL until a test connection with the settings succeeds.
 */
export function AtbWeightDatabasePanel({ locationId }: { locationId: string }) {
  const licensed = useHasModule('weight');
  const canRead = useHasPermission('weight.read');
  if (!licensed || !canRead) return null;
  return <AtbWeightDatabaseForm key={locationId} locationId={locationId} />;
}

function AtbWeightDatabaseForm({ locationId }: { locationId: string }) {
  const t = useT('admin_ui');
  const canEdit = useHasPermission('weight.admin');
  const query = useAtbWeightDatabase(locationId, true);
  const save = useSaveAtbWeightDatabase(locationId);
  const test = useTestAtbWeightDatabase(locationId);
  const clear = useClearAtbWeightDatabasePassword(locationId);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [password, setPassword] = useState('');
  const [replacing, setReplacing] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const view = query.data;
  useEffect(() => {
    if (view !== undefined) setDraft(draftOf(view));
  }, [view?.updatedAt, view?.passwordSet]);

  if (query.isLoading || draft === null || view === undefined) {
    return query.isError ? (
      <p className="text-sm text-destructive">
        {t(
          'locations.weightDatabase.loadError',
          'The weight database setting could not be loaded.',
        )}
      </p>
    ) : null;
  }

  const current: Draft = draft;
  const set = (patch: Partial<Draft>) => setDraft({ ...current, ...patch });
  const mssql = draft.mode === 'mssql';
  const typedPassword = password === '' ? undefined : password;

  async function onTest() {
    setMessage(null);
    try {
      const result = await test.mutateAsync({
        server: optional(current.server),
        port: integer(current.port),
        databaseName: optional(current.databaseName),
        login: optional(current.login),
        password: typedPassword,
      });
      setMessage(
        result.ok
          ? { ok: true, text: t('locations.weightDatabase.testOk', 'Connection succeeded.') }
          : { ok: false, text: result.message },
      );
    } catch (err) {
      setMessage({
        ok: false,
        text: errorText(err) ?? t('locations.weightDatabase.testFailed', 'The test could not run.'),
      });
    }
  }

  async function onSave() {
    setMessage(null);
    const input: AtbWeightDatabaseSettingsInput = {
      mode: current.mode,
      server: optional(current.server),
      port: integer(current.port),
      databaseName: optional(current.databaseName),
      login: optional(current.login),
      password: typedPassword,
      refreshIntervalMinutes: integer(current.refreshIntervalMinutes),
      writeBack: current.writeBack,
    };
    try {
      await save.mutateAsync(input);
      setPassword('');
      setReplacing(false);
      setMessage({ ok: true, text: t('locations.weightDatabase.saved', 'Saved.') });
    } catch (err) {
      setMessage({
        ok: false,
        text: errorText(err) ?? t('locations.weightDatabase.saveFailed', 'Not saved.'),
      });
    }
  }

  async function onClear() {
    setMessage(null);
    try {
      await clear.mutateAsync();
      setPassword('');
      setReplacing(false);
    } catch (err) {
      setMessage({
        ok: false,
        text: errorText(err) ?? t('locations.weightDatabase.saveFailed', 'Not saved.'),
      });
    }
  }

  const status = view.status;
  const stateText: Record<typeof status.state, string> = {
    off: t('locations.weightDatabase.state.off', 'Lanes use their own weights.'),
    neverRefreshed: t('locations.weightDatabase.state.neverRefreshed', 'Not read yet.'),
    ok: t('locations.weightDatabase.state.ok', 'Read successfully.'),
    error: t('locations.weightDatabase.state.error', 'The last read failed.'),
    schemaNotConfigured: t(
      'locations.weightDatabase.state.schemaNotConfigured',
      'WinSCOServer schema not configured.',
    ),
  };
  const showPasswordInput = !view.passwordSet || replacing;

  return (
    <section
      className="space-y-3 rounded-md border border-border bg-card p-4"
      data-testid="atb-weight-database"
    >
      <div>
        <h2 className="text-sm font-semibold">
          {t('locations.weightDatabase.title', 'Weight database (self-checkout)')}
        </h2>
        <p className="max-w-3xl text-xs text-muted-foreground">
          {t(
            'locations.weightDatabase.description',
            'The one database the store’s self-checkout lanes check item weights against. Lanes apply a change at their next start.',
          )}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="atb-weight-mode">
            {t('locations.weightDatabase.modeLabel', 'Database')}
          </Label>
          <Select
            id="atb-weight-mode"
            value={draft.mode}
            disabled={!canEdit}
            onChange={(e) => set({ mode: e.target.value as AtbWeightDatabaseMode })}
            className="mt-1"
          >
            <option value="sqlite">
              {t('locations.weightDatabase.mode.sqlite', 'SQLite (lane)')}
            </option>
            <option value="mssql">
              {t('locations.weightDatabase.mode.mssql', 'Microsoft SQL (WinSCOServer)')}
            </option>
          </Select>
        </div>
      </div>

      {mssql && (
        <div className="grid grid-cols-2 gap-4" data-testid="atb-weight-mssql">
          <div>
            <Label htmlFor="atb-weight-server">
              {t('locations.weightDatabase.server', 'Server')}
            </Label>
            <Input
              id="atb-weight-server"
              value={draft.server}
              disabled={!canEdit}
              onChange={(e) => set({ server: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="atb-weight-port">{t('locations.weightDatabase.port', 'Port')}</Label>
            <Input
              id="atb-weight-port"
              value={draft.port}
              disabled={!canEdit}
              onChange={(e) => set({ port: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="atb-weight-database-name">
              {t('locations.weightDatabase.databaseName', 'Weight database name')}
            </Label>
            <Input
              id="atb-weight-database-name"
              value={draft.databaseName}
              disabled={!canEdit}
              onChange={(e) => set({ databaseName: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="atb-weight-login">{t('locations.weightDatabase.login', 'Login')}</Label>
            <Input
              id="atb-weight-login"
              value={draft.login}
              disabled={!canEdit}
              onChange={(e) => set({ login: e.target.value })}
              className="mt-1"
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor="atb-weight-password">
              {t('locations.weightDatabase.passwordLabel', 'Password')}
            </Label>
            {showPasswordInput ? (
              <Input
                id="atb-weight-password"
                type="password"
                value={password}
                disabled={!canEdit}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                autoComplete="new-password"
              />
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-sm" data-testid="atb-weight-password-mask">
                  {PASSWORD_MASK}
                </span>
                {canEdit && (
                  <>
                    <Button variant="outline" onClick={() => setReplacing(true)}>
                      {t('locations.weightDatabase.password.replace', 'Replace')}
                    </Button>
                    <Button variant="outline" onClick={onClear} disabled={clear.isPending}>
                      {t('locations.weightDatabase.password.clear', 'Clear')}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="atb-weight-interval">
              {t('locations.weightDatabase.refreshInterval', 'Refresh interval (minutes)')}
            </Label>
            <Input
              id="atb-weight-interval"
              value={draft.refreshIntervalMinutes}
              disabled={!canEdit}
              onChange={(e) => set({ refreshIntervalMinutes: e.target.value })}
              className="mt-1"
            />
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.writeBack}
              disabled={!canEdit}
              onChange={(e) => set({ writeBack: e.target.checked })}
            />
            {t(
              'locations.weightDatabase.writeBack',
              'Write learned weights to WinSCOServer (not available yet)',
            )}
          </label>
          <div className="col-span-2 text-xs text-muted-foreground" data-testid="atb-weight-status">
            <p>{stateText[status.state]}</p>
            {status.lastRefreshOkAt !== null && (
              <p>
                {t('locations.weightDatabase.lastRefresh', 'Last successful read')}:{' '}
                {new Date(status.lastRefreshOkAt).toLocaleString()} ·{' '}
                {t('locations.weightDatabase.records', 'Records')}: {status.recordCount}
              </p>
            )}
            {status.lastError !== null && status.state !== 'schemaNotConfigured' && (
              <p className="text-destructive">{status.lastError}</p>
            )}
          </div>
        </div>
      )}

      {message !== null && (
        <p className={message.ok ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}>
          {message.text}
        </p>
      )}

      {canEdit && (
        <div className="flex gap-2">
          {mssql && (
            <Button variant="outline" onClick={onTest} disabled={test.isPending}>
              {t('locations.weightDatabase.test', 'Test connection')}
            </Button>
          )}
          <Button onClick={onSave} disabled={save.isPending}>
            {save.isPending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </Button>
        </div>
      )}
    </section>
  );
}
